import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { BinaryReaderService, RequestRoot, uFormat } from 'types';
import { AppContext } from '../../base/control/context';
import { FeaturesI18N } from '../../base/model/features-i18n';
import { ImageProcessor } from '../../service/image-processor';

export abstract class PhotoUploadCallback {
  abstract confirmPhotoDeletion(): Observable<boolean>;
}

@Component({
  selector: 'i-photo-upload',
  templateUrl: './photo-upload.component.html',
  styleUrls: ['./photo-upload.component.scss'],
})
export class PhotoUploadComponent implements OnInit {
  private appContext: AppContext;
  private sanitizer: DomSanitizer;
  private imageProcessor: ImageProcessor;
  private binaryReaderService: BinaryReaderService;
  private callback: PhotoUploadCallback;
  private imageTagName: string = 'applicant.printPhoto';
  private readonly MEGABYTE = 1024 * 1024;

  @Input() model: RequestRoot;
  public imageSource: SafeUrl | null;
  public fileInputValue: string | null;
  public errorMessage: string | null = null;

  constructor(sanitizer: DomSanitizer, imageProcessor: ImageProcessor, callback: PhotoUploadCallback, appContext: AppContext) {
    this.callback = callback;
    this.sanitizer = sanitizer;
    this.appContext = appContext;
    this.imageProcessor = imageProcessor;
    this.binaryReaderService = new BinaryReaderService();
  }

  ngOnInit(): void {
    let url = this.model.readBlobURL(this.imageTagName);
    if (url) {
      this.imageSource = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
  }

  uploadImage($event: any) {
    let file: File = $event.target.files[0];
    let sizeInMB = file.size / this.MEGABYTE;
    if (sizeInMB > 4) {
      this.errorMessage = uFormat(FeaturesI18N.getString('fail.photo.upload.oversized'), sizeInMB.toFixed(2));
      console.error('Oversized file upload. Size: ' + sizeInMB.toFixed(2) + 'MB.');
      return;
    }

    this.errorMessage = null;
    this.imageProcessor
      .process(file)
      .pipe(concatMap((blob) => this.binaryReaderService.readAsUrlString(blob)))
      .subscribe(
        (url) => {
          this.onPhotoUploadResult(url);
        },
        (e) => this.onPhotoUploadError(e),
      );
  }

  deleteImage() {
    this.callback.confirmPhotoDeletion().subscribe((confirmed) => {
      if (confirmed) {
        this.imageSource = null;
        this.errorMessage = null;
        this.fileInputValue = null;
        this.model.writeBlob(this.imageTagName);
        this.appContext.storeDraft(this.model);
      }
    });
  }

  private onPhotoUploadResult(url: string) {
    this.imageSource = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.model.writeBlob(this.imageTagName, url);
    this.appContext.storeDraft(this.model);
  }

  private onPhotoUploadError(error: any) {
    let key = error.code === 'INVALID_IMAGE_FORMAT' ? 'fail.photo.upload.format' : 'fail.photo.upload';
    this.errorMessage = FeaturesI18N.getString(key);
  }
}
